import React, { useState } from 'react';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { Box } from '@material-ui/core';
import { useTranslation } from '../../../../hooks/useTranslation';
import colors from '../../../../themes/main-colors';
import NestedOutageImg from '../../../../images/nested-outage-bg.png';
import OutageStatusGreen from '../../../../static/outageIconsV2/outage_status_green.svg';
import OutageStatusRed from '../../../../static/outageIconsV2/outage_status_red.svg';
import { PowerStatusDialog } from '../power-status-dialog';
import { useIsMobile } from '../../../../util/style-utils';

type Step = {
  id: number;
  type: 'servicelocation' | 'transformer' | 'line' | 'substation';
  status: 'success' | 'failed';
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    illustrationContainer: ({ height = 184 }: { height?: number }) => ({
      backgroundColor: colors.riverBlue,
      height,
      position: 'relative',
      cursor: 'pointer',
    }),
    statusBarWrapper: {
      position: 'absolute',
      bottom: 21,
      left: 0,
      right: 0,
    },
    statusBarContainer: {
      display: 'flex',
      alignItems: 'center',
    },
    line: {
      height: 4,
      flex: 1,
    },
    lineFixedWidth: {
      width: 34,
      height: 4,
    },
    successLine: {
      backgroundColor: theme.palette.success.main,
    },
    failedLine: {
      backgroundColor: theme.palette.error.main,
    },
  }),
);

export const OutageStatusGraphic = ({
  step,
  hasKnownOutage,
  height = { md: 184, sm: 220 },
}: {
  step: number;
  hasKnownOutage?: boolean | null;
  height?: { md: number; sm: number };
}) => {
  const isMobile = useIsMobile();
  const classes = useStyles({ height: isMobile ? height.sm : height.md });
  const { t } = useTranslation();
  const [open, setOpen] = useState<boolean>(false);

  return (
    <>
      <div
        className={classes.illustrationContainer}
        onClick={() => setOpen(true)}
      >
        <Box>
          <img src={NestedOutageImg} width={'100%'} />
        </Box>

        <div className={classes.statusBarWrapper}>
          <Box width="100%">
            {/* TODO: Hardcoded step value. Will fix this post-go-live */}
            <NestedOutageStatusBar
              steps={[
                {
                  id: 1,
                  type: 'servicelocation',
                  status: 'success',
                },
                { id: 2, type: 'transformer', status: 'success' },
                { id: 3, type: 'line', status: 'success' },
                {
                  id: 4,
                  type: 'substation',
                  status: hasKnownOutage ? 'failed' : 'success',
                },
              ]}
            />
          </Box>
        </div>
      </div>
      <PowerStatusDialog open={open} setOpen={setOpen} />
    </>
  );
};

type NestedOutageStatusBarProps = {
  steps: Step[];
};

const NestedOutageStatusBar: React.FC<NestedOutageStatusBarProps> = ({
  steps,
}) => {
  const isMobile = useIsMobile();
  const classes = useStyles({});
  const failedIndex = steps.findIndex(s => s.status === 'failed');

  return (
    <Box className={classes.statusBarContainer}>
      {steps.map((step, index) => {
        const isFailedOrAfter = failedIndex !== -1 && index >= failedIndex;
        const lineClass = isFailedOrAfter
          ? classes.failedLine
          : classes.successLine;

        return (
          <React.Fragment key={step.id}>
            {index === 0 && (
              <Box className={`${classes.lineFixedWidth} ${lineClass}`} />
            )}

            {isFailedOrAfter ? (
              <OutageStatusRed width={24} height={24} />
            ) : (
              <OutageStatusGreen width={24} height={24} />
            )}

            {index === steps.length - 1 ? (
              <Box className={`${classes.lineFixedWidth} ${lineClass}`} />
            ) : (
              <Box className={`${classes.line} ${lineClass}`} />
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
};
